import type {
  ConsultantKpi,
  ConsultantKpiMetrics,
  ConsultantKpiObjectives,
} from "@asap/shared/schemas/team-member-data";
import { format, parse } from "date-fns";
import { fr } from "date-fns/locale";
import { capitalize } from "lodash-es";
import {
  achievementPercent,
  achievementStatus,
  deltaStatus,
  formatEuro,
  formatEuroWithDecimals,
  formatNumber,
  formatPercent,
  formatSignedValue,
  MISSING_KPI_VALUE,
  resolvePldEmptyDescription,
} from "~/components/consultant-kpi/theme";
import type {
  ConsultantKpiViewModel,
  FunnelBlock,
  HighlightItem,
  KpiTile,
  RetentionRecommendation,
  TrajectoryHeader,
} from "~/components/consultant-kpi/types";

/** Comparison against the previous period; absent when that period holds no value to compare with. */
const toComparison = (params: {
  achieved: number;
  previousAchieved?: number | null;
  formatValue: (value: number) => string;
}): Pick<KpiTile, "delta" | "deltaStatus" | "comparison"> => {
  if (params.previousAchieved === undefined || params.previousAchieved === null) return {};

  const difference = params.achieved - params.previousAchieved;

  return {
    delta: formatSignedValue(difference, params.formatValue),
    deltaStatus: deltaStatus(difference),
    comparison: `vs ${params.formatValue(params.previousAchieved)} préc.`,
  };
};

const toTile = (params: {
  label: string;
  achieved: number;
  previousAchieved?: number | null;
  target: number;
  formatValue?: (value: number) => string;
  formatTarget?: (target: number) => string;
}): KpiTile => {
  const progressPercent = achievementPercent(params.achieved, params.target);
  const formatValue = params.formatValue ?? ((value: number) => formatNumber(value));
  const { delta, deltaStatus, comparison } = toComparison({ ...params, formatValue });

  return {
    label: params.label,
    value: formatValue(params.achieved),
    delta,
    deltaStatus,
    comparison,
    progressPercent,
    status: achievementStatus(progressPercent),
    target:
      params.formatTarget?.(params.target) ??
      `Obj. ${formatNumber(params.target, Number.isInteger(params.target) ? 0 : 1)}`,
  };
};

const toMissingTile = (params: {
  label: string;
  target: number;
  formatTarget?: (target: number) => string;
}): KpiTile => ({
  label: params.label,
  value: MISSING_KPI_VALUE,
  progressPercent: 0,
  target:
    params.formatTarget?.(params.target) ??
    `Obj. ${formatNumber(params.target, Number.isInteger(params.target) ? 0 : 1)}`,
  status: "neutral",
  missing: true,
});

const toNullableTile = (params: {
  label: string;
  achieved: number | null;
  previousAchieved?: number | null;
  target: number;
  formatValue: (value: number) => string;
  formatTarget?: (target: number) => string;
}): KpiTile => {
  if (params.achieved === null) {
    return toMissingTile(params);
  }

  return toTile({ ...params, achieved: params.achieved });
};

const toUnavailableTile = (label: string): KpiTile => ({
  label,
  value: "",
  progressPercent: 0,
  target: "",
  status: "neutral",
  unavailable: true,
});

const toFunnel = (params: {
  title: string;
  subtitle: string;
  missionLabel: string;
  placementLabel: string;
  conversionLabel: string;
  missionCount: number;
  placementCount: number;
  missionTarget: number;
  placementTarget: number;
}): FunnelBlock => ({
  title: params.title,
  subtitle: params.subtitle,
  steps: [
    {
      label: params.missionLabel,
      value: params.missionCount,
      targetValue: params.missionTarget,
      achievementPercent: achievementPercent(params.missionCount, params.missionTarget),
    },
    {
      label: params.placementLabel,
      value: params.placementCount,
      targetValue: params.placementTarget,
      achievementPercent: achievementPercent(params.placementCount, params.placementTarget),
    },
  ],
  conversionLabel: params.conversionLabel,
  conversionValue: `${formatNumber(
    params.missionCount === 0 ? 0 : params.placementCount / params.missionCount,
    1
  )} TT/cde`,
});

const toPeriodLabel = (kpi: ConsultantKpi) =>
  capitalize(
    format(new Date(kpi.periodStartDate), kpi.period === "month" ? "MMMM yyyy" : "d MMM yyyy", { locale: fr })
  );

const toYearMonthPeriodLabel = (periodYearMonth: string) =>
  capitalize(format(parse(periodYearMonth, "yyyyMM", new Date()), "MMM yy", { locale: fr }));

const toTrajectoryPointLabel = (kpiPeriod: ConsultantKpi["period"], pointPeriod: string) =>
  kpiPeriod === "week"
    ? capitalize(format(parse(pointPeriod, "yyyy-MM-dd", new Date()), "EEE", { locale: fr }))
    : toYearMonthPeriodLabel(pointPeriod);

const derivePlanningMetrics = (kpi: ConsultantKpiMetrics) => {
  const { temporaryWorkerCount, fullTimeEquivalent, averageCoefficient, averageHourlyWage } = kpi.planning;

  return {
    occupancyPercent: temporaryWorkerCount === 0 ? null : (fullTimeEquivalent / temporaryWorkerCount) * 100,
    temporaryWorkerPerFullTimeEquivalentRatio:
      fullTimeEquivalent === 0 ? null : temporaryWorkerCount / fullTimeEquivalent,
    averageBilledHourlyRate: averageHourlyWage === null ? null : averageHourlyWage * (averageCoefficient ?? 1),
    ttOnPlanningDetail: `${formatNumber(temporaryWorkerCount)} TT pour ${formatNumber(fullTimeEquivalent, 1)} ETP`,
  };
};

export const mapConsultantKpiToViewModel = (
  kpi: ConsultantKpi,
  objectives: ConsultantKpiObjectives
): ConsultantKpiViewModel => {
  const planningMetrics = derivePlanningMetrics(kpi);
  const previous = kpi.previousPeriod;
  const previousPlanningMetrics = previous === null ? null : derivePlanningMetrics(previous);
  const previousNetMarginRate = previous?.pld.status === "success" ? previous.pld.netMarginRate : null;
  const bucketCount = kpi.calls.trend.length;
  const bucketTarget = bucketCount === 0 ? 0 : Math.round(objectives.talentCalls / bucketCount);

  const trajectoryKpis: [KpiTile, KpiTile, KpiTile] = [
    toTile({
      label: "TT au planning",
      achieved: kpi.planning.temporaryWorkerCount,
      previousAchieved: previous?.planning.temporaryWorkerCount,
      target: objectives.temporaryWorkersOnPlanning,
    }),
    toTile({
      label: "ETP",
      achieved: kpi.planning.fullTimeEquivalent,
      previousAchieved: previous?.planning.fullTimeEquivalent,
      target: objectives.fullTimeEquivalent,
      formatValue: (value) => formatNumber(value, 1),
    }),
    toNullableTile({
      label: "Coefficient",
      achieved: kpi.planning.averageCoefficient,
      previousAchieved: previous?.planning.averageCoefficient,
      target: objectives.coefficient,
      formatValue: (value) => formatNumber(value, 2),
    }),
  ];

  const billedRateGap =
    planningMetrics.averageBilledHourlyRate === null
      ? null
      : planningMetrics.averageBilledHourlyRate - objectives.billedHourlyRate;
  const missingRecentBench = objectives.recentBenchReplacements - kpi.contracts.withTalentPreviousEndAtLeast30DaysCount;
  const missingDormantBench =
    objectives.dormantBenchReactivations - kpi.contracts.withTalentPreviousEndAtLeast120DaysCount;
  const retentionRecommendations: RetentionRecommendation[] = [];

  if (missingRecentBench > 0) {
    retentionRecommendations.push({
      badge: String(missingRecentBench),
      status: "warning",
      text: `Encore ${missingRecentBench} intérimaire(s) du banc récent à replacer.`,
    });
  }
  if (missingDormantBench > 0) {
    retentionRecommendations.push({
      badge: String(missingDormantBench),
      status: "critical",
      text: `Encore ${missingDormantBench} intérimaire(s) du banc dormant à réveiller.`,
    });
  }
  if (retentionRecommendations.length === 0) {
    retentionRecommendations.push({ badge: "", status: "success", text: "Objectifs de réactivation atteints." });
  }

  const isPldAvailable = kpi.pld.status === "success";
  const pldUnavailable = !isPldAvailable;
  const pldEmptyDescription = resolvePldEmptyDescription(kpi.pld.errorKey);
  const netMargin = kpi.pld.netMargin;
  const netMarginRate = kpi.pld.netMarginRate;

  const netMarginHighlight: HighlightItem =
    isPldAvailable && netMargin !== null
      ? {
          label: "Marge nette",
          status: achievementStatus(achievementPercent(netMargin, objectives.netMargin)),
        }
      : { label: "Marge nette", status: "neutral", missing: true };

  const netMarginRateHighlight: HighlightItem =
    isPldAvailable && netMarginRate !== null
      ? {
          label: "Taux de marge",
          status: achievementStatus(achievementPercent(netMarginRate, objectives.netMarginRate)),
        }
      : { label: "Taux de marge", status: "neutral", missing: true };

  const trajectoryPoints = kpi.pld.netMarginTrend.map((point) => ({
    label: toTrajectoryPointLabel(kpi.period, point.period),
    value: point.netMargin,
  }));
  const previousNetMargin = trajectoryPoints.length >= 2 ? trajectoryPoints[trajectoryPoints.length - 2].value : null;

  const trajectory: TrajectoryHeader | undefined =
    isPldAvailable && netMargin !== null
      ? {
          title: "Trajectoire",
          seriesLabel: "Marge nette mensuelle",
          targetLabel: `Obj. ${formatEuro(objectives.netMargin)}`,
          targetValue: objectives.netMargin,
          achievementPercent: achievementPercent(netMargin, objectives.netMargin),
          currentValue: netMargin,
          delta: previousNetMargin === null ? netMargin - objectives.netMargin : netMargin - previousNetMargin,
          comparison: previousNetMargin === null ? "vs objectif mensuel" : `vs ${formatEuro(previousNetMargin)} préc.`,
          points: trajectoryPoints,
        }
      : undefined;

  const profitability =
    isPldAvailable && netMarginRate !== null
      ? {
          displayValue: formatPercent(netMarginRate, 1),
          delta:
            previousNetMarginRate === null
              ? netMarginRate - objectives.netMarginRate
              : netMarginRate - previousNetMarginRate,
          comparison:
            previousNetMarginRate === null
              ? "vs objectif mensuel"
              : `vs ${formatPercent(previousNetMarginRate, 1)} préc.`,
          achievementPercent: achievementPercent(netMarginRate, objectives.netMarginRate),
          target: `Obj. ${formatNumber(objectives.netMarginRate)}`,
          status: achievementStatus(achievementPercent(netMarginRate, objectives.netMarginRate)),
        }
      : undefined;

  const firstContractStartsAchievementPercent = achievementPercent(
    kpi.contracts.withFirstContractTalentCount,
    objectives.firstContractStarts
  );

  return {
    periodLabel: toPeriodLabel(kpi),
    highlights: [
      ...trajectoryKpis.map(({ label, status, unavailable }) => ({ label, status, unavailable })),
      netMarginHighlight,
      netMarginRateHighlight,
      {
        label: "Productivité",
        status: achievementStatus(achievementPercent(kpi.calls.talentCallCount, objectives.talentCalls)),
      },
    ],
    levers: [
      {
        badge:
          planningMetrics.occupancyPercent === null
            ? MISSING_KPI_VALUE
            : formatPercent(planningMetrics.occupancyPercent),
        status:
          planningMetrics.occupancyPercent === null
            ? "neutral"
            : achievementStatus(achievementPercent(planningMetrics.occupancyPercent, objectives.occupancyPercent)),
        title: `Ratio TT/ETP ${
          planningMetrics.temporaryWorkerPerFullTimeEquivalentRatio === null
            ? MISSING_KPI_VALUE
            : formatNumber(planningMetrics.temporaryWorkerPerFullTimeEquivalentRatio, 1)
        }`,
        description: `${planningMetrics.ttOnPlanningDetail} sur la période.`,
        progressPercent: planningMetrics.occupancyPercent ?? undefined,
      },
      {
        badge: billedRateGap === null ? MISSING_KPI_VALUE : `${formatNumber(billedRateGap, 1)}€`,
        status: billedRateGap === null ? "neutral" : billedRateGap >= 0 ? "success" : "critical",
        title: "Taux horaire facturé",
        description: `${
          planningMetrics.averageBilledHourlyRate === null
            ? MISSING_KPI_VALUE
            : formatEuroWithDecimals(planningMetrics.averageBilledHourlyRate)
        } contre ${formatNumber(objectives.billedHourlyRate, 1)}€ visés.`,
      },
      {
        badge: formatPercent(firstContractStartsAchievementPercent),
        status: achievementStatus(firstContractStartsAchievementPercent),
        title: "Démarrages",
        description: `${formatNumber(kpi.contracts.withFirstContractTalentCount)} nouveau(x) intérimaire(s) sur ${formatNumber(
          objectives.firstContractStarts
        )} attendu(s).`,
        progressPercent: firstContractStartsAchievementPercent,
      },
    ],
    trajectoryKpis,
    productivityKpis: [
      toTile({
        label: "Appels talent",
        achieved: kpi.calls.talentCallCount,
        previousAchieved: previous?.calls.talentCallCount,
        target: objectives.talentCalls,
      }),
    ],
    callsTrend: {
      label: kpi.period === "month" ? "Tendance hebdo" : "Tendance quotidienne",
      points: kpi.calls.trend.map((point) => ({
        label: format(new Date(point.startDate), kpi.period === "month" ? "d MMM" : "EEE", { locale: fr }),
        column: point.talentCallCount,
        line: bucketTarget,
      })),
    },
    temporaryWorkerAcquisitionKpis: [
      toUnavailableTile("CV qualifiés & disponibles"),
      toTile({
        label: "Démarrages",
        achieved: kpi.contracts.withFirstContractTalentCount,
        previousAchieved: previous?.contracts.withFirstContractTalentCount,
        target: objectives.firstContractStarts,
      }),
    ],
    retention: {
      title: "Fidélisation",
      subtitle: "Rétention et réactivation du vivier",
      rows: [
        toNullableTile({
          label: "Rétention du planning",
          achieved: kpi.contracts.planningRetentionPercent,
          previousAchieved: previous?.contracts.planningRetentionPercent,
          target: objectives.planningRetentionPercent,
          formatValue: (value) => formatPercent(value),
          formatTarget: (target) => `Obj. ${formatPercent(target)}`,
        }),
        toTile({
          label: "Banc récent à replacer",
          achieved: kpi.contracts.withTalentPreviousEndAtLeast30DaysCount,
          previousAchieved: previous?.contracts.withTalentPreviousEndAtLeast30DaysCount,
          target: objectives.recentBenchReplacements,
        }),
        toTile({
          label: "Banc dormant réveillé",
          achieved: kpi.contracts.withTalentPreviousEndAtLeast120DaysCount,
          previousAchieved: previous?.contracts.withTalentPreviousEndAtLeast120DaysCount,
          target: objectives.dormantBenchReactivations,
        }),
      ],
      recommendations: retentionRecommendations,
    },
    clientAcquisition: toFunnel({
      title: "Acquisition clients",
      subtitle: "New business",
      missionLabel: "Commandes new business",
      placementLabel: "TT placés (nouveaux)",
      conversionLabel: "Taux de transformation",
      missionCount: kpi.missions.newCompanyCount,
      placementCount: kpi.contracts.newTalentPlacementCount,
      missionTarget: objectives.newCompanyMissions,
      placementTarget: objectives.newTalentPlacements,
    }),
    clientLoyalty: toFunnel({
      title: "Fidélisation clients",
      subtitle: "Approfondissement des comptes",
      missionLabel: "Commandes comptes existants",
      placementLabel: "TT placés (existants)",
      conversionLabel: "Approfondissement commande",
      missionCount: kpi.missions.existingCompanyCount,
      placementCount: kpi.contracts.existingTalentPlacementCount,
      missionTarget: objectives.existingCompanyMissions,
      placementTarget: objectives.existingTalentPlacements,
    }),
    planningEconomy: {
      title: "Économie du planning",
      ratio: {
        value:
          planningMetrics.temporaryWorkerPerFullTimeEquivalentRatio === null
            ? MISSING_KPI_VALUE
            : formatNumber(planningMetrics.temporaryWorkerPerFullTimeEquivalentRatio, 1),
        label: "Ratio TT / ETP",
        detail: planningMetrics.ttOnPlanningDetail,
      },
      occupancy: {
        label: "Occupation",
        percent: planningMetrics.occupancyPercent === null ? null : Math.round(planningMetrics.occupancyPercent),
      },
      kpis: [
        toNullableTile({
          label: "Taux horaire facturé",
          achieved: planningMetrics.averageBilledHourlyRate,
          previousAchieved: previousPlanningMetrics?.averageBilledHourlyRate,
          target: objectives.billedHourlyRate,
          formatValue: (value) => formatEuroWithDecimals(value),
        }),
        toNullableTile({
          label: "Coefficient moyen",
          achieved: kpi.planning.averageCoefficient,
          previousAchieved: previous?.planning.averageCoefficient,
          target: objectives.coefficient,
          formatValue: (value) => formatNumber(value, 2),
        }),
        isPldAvailable && netMarginRate !== null
          ? toNullableTile({
              label: "Taux de marge nette",
              achieved: netMarginRate,
              previousAchieved: previousNetMarginRate,
              target: objectives.netMarginRate,
              formatValue: (value) => formatPercent(value, 1),
              formatTarget: (target) => `Obj. ${formatPercent(target, 1)}`,
            })
          : toMissingTile({
              label: "Taux de marge nette",
              target: objectives.netMarginRate,
              formatTarget: (target) => `Obj. ${formatPercent(target, 1)}`,
            }),
      ],
    },
    trajectory,
    profitability,
    pldUnavailable,
    pldEmptyDescription,
  };
};
